import { format, formatDistanceToNow } from 'date-fns';
import { Building2, User as UserIcon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type {
  AdminUserAccountAssociation,
  AdminUserSsoIdentity,
  AdminUserSsoEvent,
} from '@/types/admin';

interface UserAccountAssociationCardProps {
  association: AdminUserAccountAssociation;
}

const PROVIDER_LABEL: Record<string, string> = {
  google: 'Google',
  azure: 'Microsoft (Azure)',
  microsoft: 'Microsoft',
  email: 'Email / password',
  magiclink: 'Magic link',
};

export function UserAccountAssociationCard({
  association,
}: UserAccountAssociationCardProps): JSX.Element {
  const isEnterprise =
    association.account_type === 'enterprise' || association.account_type === 'mixed';
  const Icon = isEnterprise ? Building2 : UserIcon;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          Account association
        </CardTitle>
        <AccountTypeBadge type={association.account_type} />
      </CardHeader>
      <CardContent className="space-y-4">
        <SignupBlock association={association} />
        {association.identities.length > 0 && (
          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Enterprise identities ({association.identities.length})
            </div>
            {association.identities.map((identity) => (
              <IdentityBlock key={identity.identity_id} identity={identity} />
            ))}
          </div>
        )}
        {association.account_type === 'mixed' && (
          <div className="text-xs text-muted-foreground border-l-2 border-amber-500/40 pl-3">
            User also belongs to {association.non_enterprise_workspace_count}{' '}
            non-enterprise workspace
            {association.non_enterprise_workspace_count === 1 ? '' : 's'}.
          </div>
        )}
        {association.recent_sso_events.length > 0 && (
          <RecentEventsBlock events={association.recent_sso_events} />
        )}
      </CardContent>
    </Card>
  );
}

function AccountTypeBadge({
  type,
}: {
  type: AdminUserAccountAssociation['account_type'];
}): JSX.Element {
  if (type === 'personal') {
    return <Badge variant="secondary">Personal</Badge>;
  }
  if (type === 'mixed') {
    return (
      <Badge variant="outline" className="border-amber-600/50 text-amber-700">
        Mixed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-blue-600/50 text-blue-700">
      Enterprise
    </Badge>
  );
}

function SignupBlock({
  association,
}: {
  association: AdminUserAccountAssociation;
}): JSX.Element {
  const { signup, account_type } = association;
  const providerLabel = PROVIDER_LABEL[signup.provider] ?? signup.provider;

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      <dt className="text-muted-foreground">Signed up</dt>
      <dd>{format(new Date(signup.created_at), 'PP')}</dd>
      <dt className="text-muted-foreground">Sign-in method</dt>
      <dd className="capitalize">{providerLabel}</dd>
      <dt className="text-muted-foreground">Last sign-in</dt>
      <dd>
        {signup.last_sign_in_at
          ? formatDistanceToNow(new Date(signup.last_sign_in_at), { addSuffix: true })
          : 'Never'}
      </dd>
      {account_type === 'personal' && (
        <>
          <dt className="text-muted-foreground">Partner</dt>
          <dd className="text-muted-foreground italic">Not associated</dd>
        </>
      )}
    </dl>
  );
}

function IdentityBlock({ identity }: { identity: AdminUserSsoIdentity }): JSX.Element {
  const isActive = identity.identity_status === 'active';
  const isPartnerHealthy = identity.partner_status === 'active';

  return (
    <div className="rounded-md border border-border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{identity.partner_name}</div>
          <div className="text-xs text-muted-foreground">
            {identity.partner_slug} · {identity.provisioning_mode} provisioning
          </div>
        </div>
        <Badge
          variant="outline"
          className={
            isActive
              ? 'border-emerald-600/50 text-emerald-700'
              : 'border-destructive/50 text-destructive'
          }
        >
          {identity.identity_status}
        </Badge>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">External ID</dt>
        <dd className="font-mono truncate" title={identity.external_user_id}>
          {identity.external_user_id}
        </dd>
        {identity.partner_email && (
          <>
            <dt className="text-muted-foreground">Partner email</dt>
            <dd className="truncate">{identity.partner_email}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Provisioned</dt>
        <dd>{format(new Date(identity.provisioned_at), 'PP')}</dd>
        <dt className="text-muted-foreground">Last SSO sign-in</dt>
        <dd>
          {identity.last_login_at
            ? formatDistanceToNow(new Date(identity.last_login_at), { addSuffix: true })
            : 'Never'}
        </dd>
        {identity.revoked_at && (
          <>
            <dt className="text-muted-foreground">Revoked</dt>
            <dd>{format(new Date(identity.revoked_at), 'PP')}</dd>
          </>
        )}
        {identity.provisioned_workspace && (
          <>
            <dt className="text-muted-foreground">Workspace</dt>
            <dd className="truncate">
              {identity.provisioned_workspace.name ?? '(deleted)'}
            </dd>
          </>
        )}
      </dl>
      {!isPartnerHealthy && (
        <div className="text-xs text-amber-700 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Partner is {identity.partner_status}
        </div>
      )}
    </div>
  );
}

function RecentEventsBlock({ events }: { events: AdminUserSsoEvent[] }): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Recent SSO events
      </div>
      <div className="space-y-1">
        {events.map((event, i) => (
          <EventRow key={`${event.created_at}-${i}`} event={event} />
        ))}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: AdminUserSsoEvent }): JSX.Element {
  const isFailure = event.status === 'failure';
  const Icon = isFailure ? AlertTriangle : CheckCircle2;
  const iconClass = isFailure ? 'text-destructive' : 'text-emerald-600';

  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={`h-3 w-3 shrink-0 ${iconClass}`} />
      <span
        className="text-muted-foreground tabular-nums shrink-0"
        title={format(new Date(event.created_at), 'PPpp')}
      >
        {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
      </span>
      <span className="font-mono truncate">{event.event_type}</span>
      {event.partner_name && (
        <span className="text-muted-foreground truncate">· {event.partner_name}</span>
      )}
      {isFailure && event.error_code && (
        <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px] h-4 px-1">
          {event.error_code}
        </Badge>
      )}
    </div>
  );
}
